import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, SearchSchemaInput } from '@tanstack/react-router'
import { DataTable } from "@/components/features/issues/data-table"
import { columns } from "@/components/features/issues/columns"
import { DatePicker } from "@/components/ui/datepicker"
import { useEffect, useState } from 'react'
import { useStore } from '@/store'
import { useShallow } from 'zustand/shallow'
import { Loader2, RefreshCw } from 'lucide-react'
import { Issue } from '@/lib/interfaces'
import { Api } from '@/lib/utils'
import dayjs from 'dayjs'
import { Button } from '@/components/ui/button'
import { Pagination } from '@/components/ui/pagination'
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner'

export const Route = createFileRoute('/_auth/issues/')({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown> & SearchSchemaInput): { date: string } => {
    return {
      date: search.date as string || ''
    }
  }
})

function RouteComponent() {
  const { token, setMainPage } = useStore(useShallow(state => ({
    token: state.token,
    setMainPage: state.setPage
  })))

  const [page, setPage] = useState(1)
  const { date } = Route.useSearch()

  useEffect(() => {
    setMainPage('Issues')
  }, [])

  const recordsPerPage = 100
  const navigate = useNavigate({ from: Route.fullPath })
  const queryClient = useQueryClient()

  const getIssues = useQuery({
    queryKey: ['issues', date, token],
    queryFn: () => Api<Issue[]>('/webui/issues_detailed', "POST", { date }, { token }),
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    refetchOnWindowFocus: false
  })

  const refreshIssue = useMutation({
    mutationFn: (issueId: string) => {
      return Api<{ status: number, issue: Issue }>('/webui/order/issue_status', "POST",
        { issue: { id: issueId } }, { token })
    },
    onError(error) {
      toast.error(error.message ||  "Error refreshing issue")
    },
    onSuccess(data, issueId, _c) {
      queryClient.setQueryData(['issues', date, token], (oldData: Issue[]) => {
        const index = (oldData||[]).findIndex(e => e.issueId === issueId)
        return index > -1 ? [
          ...oldData.slice(0, index),
          data.issue,
          ...oldData.slice(index + 1)
        ] : oldData
      })
      toast.success('Issue refreshed')
    }
  })

  if (getIssues.error) return 'An error has occurred: ' + getIssues.error.message

  return (
    <div className='fixed top-14 left-0 w-full h-full p-2 bottom-0'>
      <div className='flex items-start justify-between'>
        <div className='flex items-center mb-4 gap-5'>
          <DatePicker date={date ? dayjs(date).toDate() : undefined} onChange={date => {
            navigate({
              search: () => ({ date: dayjs(date).format('YYYY-MM-DD') }),
            })
          }} />
          {getIssues.isFetching ? <div className='flex items-center gap-3'>
            <Loader2 className='w-6 h-6 animate-spin' />
            <p className='hidden md:block'>Refreshing...</p>
          </div> : null}
        </div>
        <div className='flex items-center gap-5'>
          <Button onClick={() => {
            getIssues.refetch()
          }}>
            <RefreshCw />
            <p className='hidden lg:block'>Refresh</p>
          </Button>
        </div>
      </div>
      <div className='absolute top-16 left-1 right-1 bottom-15'>
        <div className='absolute top-0 left-0 right-0 bottom-10 lg:bottom-12 overflow-auto'>
          <DataTable
            columns={columns}
            data={(getIssues.data || []).slice((page - 1) * recordsPerPage, page * recordsPerPage)}
            isFetching={false}
            refreshIssue={issueId => refreshIssue.mutate(issueId)}
          />
        </div>
        <Pagination recordCount={(getIssues.data || []).length} page={page} setPage={page => setPage(page)} recordsPerPage={recordsPerPage} />
      </div>
    </div>
  )
}
